import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Kanban, Table as TableIcon, Calendar, CalendarBlank, Strategy, CircleNotch } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store/appStore';
import { useCatalogs } from '@/hooks/useTasks';
import { api } from '@/api/client';
import type { GroupBy } from '@/types';

// Barra secundaria de las vistas de Tareas: vive entre el TopBar y el
// contenido. Aloja el switcher de tipo de vista (Kanban / Tabla /
// Planificador / Calendario), el "Agrupar por" (solo en Kanban) y el selector
// de sprint. Se oculta en vistas que no son de tareas (dashboard, sitio,
// tramites, configuracion).
type ViewKey = 'kanban' | 'table' | 'planner' | 'calendar';

const VIEWS: { key: ViewKey; label: string; Icon: typeof Kanban }[] = [
  { key: 'kanban',   label: 'Kanban',       Icon: Kanban },
  { key: 'table',    label: 'Tabla',        Icon: TableIcon },
  { key: 'planner',  label: 'Planificador', Icon: Calendar },
  { key: 'calendar', label: 'Calendario',   Icon: CalendarBlank },
];

const GROUPINGS: { value: GroupBy; label: string }[] = [
  { value: 'status',   label: 'Por Estado' },
  { value: 'assignee', label: 'Por Responsable' },
  { value: 'project',  label: 'Por Proyecto' },
];

export function ViewToolbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const groupBy = useAppStore((s) => s.groupBy);
  const setGroupBy = useAppStore((s) => s.setGroupBy);
  const sprintId = useAppStore((s) => s.filters.sprintId);
  const setFilter = useAppStore((s) => s.setFilter);
  const planningMode = useAppStore((s) => s.planningMode);
  const setPlanningMode = useAppStore((s) => s.setPlanningMode);
  const setPlanningSprintId = useAppStore((s) => s.setPlanningSprintId);
  const { data: catalogs } = useCatalogs();
  const qc = useQueryClient();
  const [entering, setEntering] = useState(false);

  const pathname = location.pathname;
  const isKanban = pathname.startsWith('/kanban/');
  const activeView: ViewKey | null =
    isKanban ? 'kanban'
    : pathname === '/table' ? 'table'
    : pathname === '/planner' ? 'planner'
    : pathname === '/calendar' ? 'calendar'
    : null;

  // Solo se muestra en las vistas de tareas. En modo planning se oculta: el
  // PlanningBoard trae su propia barra de controles.
  if (activeView === null || planningMode) return null;

  // Entrar a planificación: resuelve el sprint objetivo (semanal del próximo
  // lunes, idempotente) y activa el modo. El tablero de planning toma la
  // pantalla; el sprint objetivo se puede cambiar desde ahí.
  async function enterPlanning() {
    setEntering(true);
    try {
      const sprint = await api.sprints.ensureWeekly();
      qc.invalidateQueries({ queryKey: ['catalogs'] });
      qc.invalidateQueries({ queryKey: ['sprints'] });
      setPlanningSprintId(sprint.idSprint);
      setPlanningMode(true);
    } catch (e: any) {
      alert(e?.message || 'No se pudo iniciar la planificación');
    } finally {
      setEntering(false);
    }
  }

  function goView(v: ViewKey) {
    if (v === 'kanban') navigate(`/kanban/${groupBy}`);
    else navigate(`/${v}`);
  }

  function changeGrouping(g: GroupBy) {
    setGroupBy(g);
    navigate(`/kanban/${g}`);
  }

  return (
    <div className="flex h-11 shrink-0 items-center gap-2 sm:gap-3 border-b border-stone-200 bg-white px-3 overflow-x-auto">
      {/* Switcher de tipo de vista. En mobile se muestran solo los iconos. */}
      <div className="flex shrink-0 items-stretch overflow-hidden rounded-md border border-stone-300">
        {VIEWS.map((v) => {
          const active = v.key === activeView;
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => goView(v.key)}
              title={v.label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                active ? 'bg-brand-green-100 text-black' : 'text-stone-600 hover:bg-stone-100'
              }`}
            >
              <v.Icon size={15} weight="bold" />
              <span className="hidden md:inline">{v.label}</span>
            </button>
          );
        })}
      </div>

      {/* Agrupar por: solo aplica al Kanban. Select nativo (como el de sprint)
          — su popup lo dibuja el SO, así no lo recorta el overflow de la barra
          (que un dropdown custom posicionado sí sufría). */}
      {isKanban && (
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
            Agrupar
          </span>
          <select
            value={groupBy}
            onChange={(e) => changeGrouping(e.target.value as GroupBy)}
            title="Agrupar por"
            className="shrink-0 cursor-pointer rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-semibold text-stone-700 hover:border-brand-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-green-100"
          >
            {GROUPINGS.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Bloque derecho: filtro de Sprint + botón Planificar. */}
      <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
        {catalogs && catalogs.sprints.length > 0 && (
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
              Sprint
            </span>
            <select
              value={sprintId ?? ''}
              onChange={(e) => setFilter('sprintId', e.target.value ? Number(e.target.value) : null)}
              title="Sprint"
              className="shrink-0 cursor-pointer rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-semibold text-stone-700 hover:border-brand-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-green-100 max-w-[110px] sm:max-w-[220px]"
            >
              <option value="">Todos los sprints</option>
              {catalogs.sprints.map((s) => (
                <option key={s.idSprint} value={s.idSprint}>{s.nombre}</option>
              ))}
            </select>
          </div>
        )}

        {/* Entrar a planificación del próximo sprint. */}
        <button
          type="button"
          onClick={enterPlanning}
          disabled={entering}
          title="Planificar el próximo sprint"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-brand-green-200 bg-brand-green-100/20 px-2.5 py-1.5 text-xs font-semibold text-brand-green-200 hover:bg-brand-green-100/40 disabled:opacity-50"
        >
          {entering
            ? <CircleNotch size={15} weight="bold" className="animate-spin" />
            : <Strategy size={15} weight="bold" />}
          <span className="hidden sm:inline">Planificar</span>
        </button>
      </div>
    </div>
  );
}
